import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, Lock, Clock, Heart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLang } from '../useLang';
import { images, bookingTrustMeta } from '../siteConfig';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from '../hooks/use-toast';

const iconMap = { Lock, Clock, Heart };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'bg-white border-black/10 h-12 rounded-lg font-body text-base focus-visible:ring-2 focus-visible:ring-[var(--navy)]';
const areaCls = 'bg-white border-black/10 rounded-lg font-body text-base resize-none focus-visible:ring-2 focus-visible:ring-[var(--navy)]';

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="font-head font-semibold text-[15px]" style={{ color: 'var(--navy)' }}>
      {label} {required && <span style={{ color: 'var(--coral)' }}>*</span>}
    </Label>
    {children}
  </div>
);

const BookingPage = () => {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const b = t.booking;

  const [form, setForm] = useState({
    name: '', email: '', organization: '', eventType: '', eventDate: '',
    location: '', audienceSize: '', lookingFor: '', topics: '', additional: '', hearAbout: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization || !form.eventType || !form.location) {
      toast({ title: b.validationTitle, description: b.validationDesc });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, lang });
      navigate(`/${lang}/thank-you`);
    } catch (err) {
      toast({
        title: b.errorTitle || 'Something went wrong',
        description: b.errorDesc || 'Please try again in a moment.',
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Header />

      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-6">
        <div className="grid md:grid-cols-[1.3fr_1fr] gap-8 items-start">
          <div className="fade-up">
            <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-tight" style={{ color: 'var(--navy)' }}>
              {b.heading}
            </h1>
            <p className="font-body text-lg mt-5 leading-relaxed" style={{ color: 'var(--ink)' }}>
              {b.sub}
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-lg hidden md:block">
            <img src={images.desk} alt="Rachelle Koulibaly" className="w-full h-52 object-cover object-top" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 md:px-8 pb-16">
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-black/[0.05] shadow-sm p-6 md:p-10">
          <h2 className="font-head font-extrabold tracking-wide text-lg mb-6" style={{ color: 'var(--navy)' }}>{b.eventDetails}</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={b.labels.name} required>
              <Input className={inputCls} placeholder={b.placeholders.name} value={form.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label={b.labels.email} required>
              <Input type="email" className={inputCls} placeholder={b.placeholders.email} value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label={b.labels.org} required>
              <Input className={inputCls} placeholder={b.placeholders.org} value={form.organization} onChange={(e) => set('organization', e.target.value)} />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <Field label={b.labels.eventType} required>
              <Select value={form.eventType} onValueChange={(v) => set('eventType', v)}>
                <SelectTrigger className={inputCls}><SelectValue placeholder={b.placeholders.eventType} /></SelectTrigger>
                <SelectContent>
                  {b.eventTypes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={b.labels.eventDate}>
              <Input type="date" className={inputCls} value={form.eventDate} onChange={(e) => set('eventDate', e.target.value)} />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <Field label={b.labels.location} required>
              <Select value={form.location} onValueChange={(v) => set('location', v)}>
                <SelectTrigger className={inputCls}><SelectValue placeholder={b.placeholders.location} /></SelectTrigger>
                <SelectContent>
                  {b.locationTypes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={b.labels.audience}>
              <Input className={inputCls} placeholder={b.placeholders.audience} value={form.audienceSize} onChange={(e) => set('audienceSize', e.target.value)} />
            </Field>
          </div>

          <h2 className="font-head font-extrabold tracking-wide text-lg mt-10 mb-6" style={{ color: 'var(--coral)' }}>{b.tellMeMore}</h2>
          <div className="flex flex-col gap-5">
            <Field label={b.labels.lookingFor}>
              <Textarea rows={4} className={areaCls} placeholder={b.placeholders.lookingFor} value={form.lookingFor} onChange={(e) => set('lookingFor', e.target.value)} />
            </Field>
            <Field label={b.labels.topics}>
              <Select value={form.topics} onValueChange={(v) => set('topics', v)}>
                <SelectTrigger className={inputCls}><SelectValue placeholder={b.placeholders.topics} /></SelectTrigger>
                <SelectContent>
                  {b.topicOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={b.labels.additional}>
              <Textarea rows={3} className={areaCls} placeholder={b.placeholders.additional} value={form.additional} onChange={(e) => set('additional', e.target.value)} />
            </Field>
          </div>

          <h2 className="font-head font-extrabold tracking-wide text-lg mt-10 mb-6" style={{ color: 'var(--coral)' }}>{b.letsConnect}</h2>
          <Field label={b.labels.hearAbout}>
            <Select value={form.hearAbout} onValueChange={(v) => set('hearAbout', v)}>
              <SelectTrigger className={inputCls}><SelectValue placeholder={b.placeholders.hearAbout} /></SelectTrigger>
              <SelectContent>
                {b.hearAbout.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <button type="submit" disabled={submitting} className="btn-coral w-full font-head font-bold tracking-wide text-lg py-4 rounded-xl mt-9 flex items-center justify-center gap-2.5 disabled:opacity-70 disabled:cursor-not-allowed">
            <Send className="w-5 h-5" /> {submitting ? (b.submitting || 'Sending…') : b.submit}
          </button>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          {b.trust.map((item, i) => {
            const meta = bookingTrustMeta[i];
            const Icon = iconMap[meta.icon];
            return (
              <div key={i} className="flex items-start gap-3">
                <Icon className="w-7 h-7 shrink-0" style={{ color: meta.color }} strokeWidth={1.7} />
                <div>
                  <p className="font-head font-bold text-sm" style={{ color: 'var(--navy)' }}>{item.title}</p>
                  <p className="font-body text-sm" style={{ color: 'var(--ink)' }}>{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer tagline={t.footer.tagline} />
    </div>
  );
};

export default BookingPage;
